import React, { useRef, useState } from "react";
import * as Text from "@/components/text";
import Image from "next/image";
import Flicking from "@egjs/react-flicking";
import "@egjs/react-flicking/dist/flicking.css";
import ReactMarkdown from "react-markdown";
import Button from "@/components/actions/Button";
import { MailIcon } from "@heroicons/react/outline";
import Link from "next/link";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/outline";
import { localeTexts } from "@/utils/locale";
import { getStrapiImage } from "@/utils/strapiFunctions";

export default function Book({ data, locale }) {
  const flickingRefs = useRef([]);
  const [indexFlickings, setIndexFlickings] = useState(
    Array(data?.length).fill(0)
  );

  const handleChangeIndex = (index, flickingIndex) => {
    flickingRefs[flickingIndex].moveTo(index);
  };

  return (
    <div className="mb-10 w-full border-t border-black">
      {data?.map((book, i) => (
        <div className="border-b border-black pb-12" key={i}>
          <div className="amb-container my-4 lg:my-8">
            <Text.Headline className="text-[26px] font-light md:text-[40px]">
              {book?.attributes?.title}
            </Text.Headline>
          </div>
          <div className="lg:amb-container lg:flex">
            <div className="hidden lg:block">
              <div>
                <Text.Headline
                  as="p"
                  className="text-[17px] font-normal text-neutral-600"
                >
                  {localeTexts[locale].AmbulanteEdiciones.Book.publishedIn}
                  &nbsp;
                  <span className="font-normal text-black">
                    {book?.attributes?.publish_year}
                  </span>
                </Text.Headline>
                <Text.Headline className="text-[17px] font-normal text-neutral-600">
                  {localeTexts[locale].AmbulanteEdiciones.Book.authors}&nbsp;
                  <span className="font-normal text-black underline">
                    {book?.attributes?.author}
                  </span>
                </Text.Headline>
              </div>
              <div className="mt-6 lg:w-[382px]">
                <Text.Headline className="text-[17px] font-normal text-neutral-600">
                  {localeTexts[locale].AmbulanteEdiciones.Book.synopsis}
                </Text.Headline>
                <ReactMarkdown className="font-body prose mt-4 text-base font-light prose-h2:uppercase">
                  {book?.attributes?.synopsis.replace(/<br>/g, "&nbsp; \n")}
                </ReactMarkdown>
              </div>
              <Link
                passHref
                target="_blank"
                href={`mailto:${book?.attributes?.store_link}?subject=${book?.attributes?.title}`}
                className="mt-5 flex w-full md:mt-8"
              >
                <Button
                  size="lg"
                  color="secondary-light"
                  label={localeTexts[locale].AmbulanteEdiciones.Book.makeOrder}
                  className="w-full font-bold md:w-fit md:px-[60px]"
                  trailingIcon={<MailIcon />}
                ></Button>
              </Link>
            </div>
            <div
              className="w-full"
              style={{
                WebkitMaskImage:
                  "linear-gradient(90deg, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 1), rgba(0, 0, 0, 1), rgba(0, 0, 0, 1), rgba(0, 0, 0, 1), rgba(0, 0, 0, 0.1))",
              }}
            >
              <Flicking
                ref={(el) => (flickingRefs[i] = el)}
                moveType="freeScroll"
                align="center"
              >
                {book?.attributes?.images?.data?.map((image, i) => {
                  const imageSrc = getStrapiImage(image, "large");
                  if (!imageSrc) return null;

                  return (
                    <Image
                      key={i}
                      className="h-[387px] w-max pl-4 md:h-[453px] md:pl-8"
                      width={image?.attributes?.width}
                      height={image?.attributes?.height}
                      sizes="100vw"
                      src={imageSrc}
                      alt="Book image"
                      draggable="false"
                    />
                  );
                })}
              </Flicking>
              <div className="mt-10 flex w-full items-center justify-center gap-8">
                <button
                  onClick={() => {
                    const newIndex =
                      indexFlickings[i] - 1 <= 0 ? 0 : indexFlickings[i] - 1;
                    setIndexFlickings((prev) => [
                      ...prev.slice(0, i),
                      newIndex,
                      ...prev.slice(i + 1),
                    ]);
                    handleChangeIndex(newIndex, i);
                  }}
                  className="mr-3 hidden lg:block"
                >
                  <ArrowLeftIcon className="h-7 w-7" />
                </button>
                <button
                  onClick={() => {
                    const newIndex =
                      indexFlickings[i] + 1 >=
                        book?.attributes?.images?.data.length - 1
                        ? book?.attributes?.images?.data.length - 1
                        : indexFlickings[i] + 1;
                    setIndexFlickings((prev) => [
                      ...prev.slice(0, i),
                      newIndex,
                      ...prev.slice(i + 1),
                    ]);
                    handleChangeIndex(newIndex, i);
                  }}
                  className="mr-3 hidden lg:block"
                >
                  <ArrowRightIcon className="h-7 w-7" />
                </button>
              </div>
            </div>
          </div>
          <div className="amb-container my-4 md:my-8">
            <div className="lg:hidden">
              <div>
                <Text.Headline
                  as="p"
                  className="text-[17px] font-normal text-neutral-600 md:text-[22px]"
                >
                  {localeTexts[locale].AmbulanteEdiciones.Book.publishedIn}
                  &nbsp;
                  <span className="font-normal text-black">
                    {book?.attributes?.publish_year}
                  </span>
                </Text.Headline>
                <Text.Headline className="text-[17px] font-normal text-neutral-600 md:text-[22px]">
                  {localeTexts[locale].AmbulanteEdiciones.Book.authors}&nbsp;
                  <span className="font-normal text-black">
                    {book?.attributes?.author}
                  </span>
                </Text.Headline>
              </div>
              <div className="mt-6">
                <Text.Headline className="text-[17px] font-normal text-neutral-600 md:text-[22px]">
                  {localeTexts[locale].AmbulanteEdiciones.Book.synopsis}
                </Text.Headline>
                <ReactMarkdown className="font-body prose text-base font-light prose-h2:uppercase md:text-[20px] lg:max-w-[100ch]">
                  {book?.attributes?.synopsis.replace(/<br>/g, "&nbsp; \n")}
                </ReactMarkdown>
              </div>
            </div>
            <Link
              passHref
              target="_blank"
              href={`mailto:${book?.attributes?.store_link}?subject=${book?.attributes?.title}`}
              className="mt-5 flex w-full md:mt-8 lg:hidden"
            >
              <Button
                size="lg"
                color="secondary-light"
                label={localeTexts[locale].AmbulanteEdiciones.Book.makeOrder}
                className="w-full font-bold md:w-fit md:px-[60px]"
                trailingIcon={<MailIcon />}
              ></Button>
            </Link>
          </div>
        </div>
      ))}
    </div>
  );
}
